import React from 'react';
import { Modal, Typography, Empty, Tag, Row, Col } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface ScoreDistributionModalProps {
    visible: boolean;
    onClose: () => void;
    statementText: string;
    responseCount: number;
    scoreDistribution: { [score: string]: number } | null;
    questionType: 'likert7' | 'likert7_with_na' | 'nps' | 'other';
    theme?: 'dark' | 'light';
}

export const ScoreDistributionModal: React.FC<ScoreDistributionModalProps> = ({
    visible,
    onClose,
    statementText,
    responseCount,
    scoreDistribution,
    questionType,
    theme = 'light'
}) => {
    const isDark = theme === 'dark';
    // Prepare data for display
    const distributionData = React.useMemo(() => {
        if (!scoreDistribution) return [];

        const data = Object.entries(scoreDistribution).map(([score, count]) => ({
            score: parseInt(score),
            count,
            percentage: responseCount > 0 ? Math.round((count / responseCount) * 100) : 0
        }));

        return data.sort((a, b) => a.score - b.score);
    }, [scoreDistribution, responseCount]);

    // Find max count for scaling
    const maxCount = React.useMemo(() => {
        return Math.max(...distributionData.map(d => d.count), 1);
    }, [distributionData]);

    // Get label for score based on question type
    const getScoreLabel = (score: number) => {
        return score.toString();
    };

    // Get small annotation label for specific scores
    const getScoreAnnotation = (score: number): string | null => {
        if (score === 0) return 'N/A';
        if (score === 1) return 'Strongly Disagree';
        if (score === 4) return 'Neutral';
        if (score === 7) return 'Strongly Agree';
        return null;
    };

    return (
        <Modal
            title={
                <span style={{ color: isDark ? '#f1f5f9' : '#1f2937' }}>
                    Score Distribution
                </span>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={600}
            closeIcon={<CloseOutlined style={{ color: isDark ? '#94a3b8' : undefined }} />}
            styles={{
                content: {
                    backgroundColor: isDark ? '#1a1f2e' : '#ffffff',
                },
                header: {
                    backgroundColor: isDark ? '#1a1f2e' : '#ffffff',
                    borderBottom: isDark ? '1px solid rgba(100, 116, 139, 0.3)' : '1px solid #e5e7eb',
                },
                body: {
                    backgroundColor: isDark ? '#1a1f2e' : '#ffffff',
                }
            }}
        >
            <div style={{ marginBottom: 16 }}>
                <Text style={{ color: isDark ? '#94a3b8' : undefined }}>{statementText}</Text>
            </div>

            <div style={{ marginBottom: 24 }}>
                <Tag color="blue">Total Responses: {responseCount}</Tag>
            </div>

            {scoreDistribution ? (
                <>
                    {/* Visual Bar Chart using divs */}
                    <div>
                        <Title level={5} style={{ color: isDark ? '#f1f5f9' : undefined }}>Distribution Chart</Title>
                        <div style={{ padding: '20px 0' }}>
                            {distributionData.map((item) => (
                                <Row key={item.score} gutter={[16, 8]} align="middle" style={{ marginBottom: 8 }}>
                                    <Col span={6} style={{ textAlign: 'right' }}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                            {getScoreAnnotation(item.score) && (
                                                <span style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#6b7280' }}>
                                                    {getScoreAnnotation(item.score)}
                                                </span>
                                            )}
                                            <Text strong style={{ color: isDark ? '#f1f5f9' : undefined }}>{getScoreLabel(item.score)}</Text>
                                        </span>
                                    </Col>
                                    <Col span={13}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            height: 24,
                                            backgroundColor: isDark ? 'rgba(100, 116, 139, 0.3)' : '#f0f0f0',
                                            borderRadius: 4,
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                height: '100%',
                                                width: `${(item.count / maxCount) * 100}%`,
                                                backgroundColor: '#F59E0B',
                                                transition: 'width 0.3s ease',
                                                minWidth: item.count > 0 ? 2 : 0
                                            }} />
                                        </div>
                                    </Col>
                                    <Col span={5}>
                                        <Text style={{ color: isDark ? '#94a3b8' : undefined }}>{item.count} ({item.percentage}%)</Text>
                                    </Col>
                                </Row>
                            ))}
                        </div>
                    </div>
                </>
            ) : (
                <Empty description="No score distribution data available" />
            )}
        </Modal>
    );
};